/**
 * The games hub (ADR 0075): today's deal of every game, as a ruled list like the puzzles.
 *
 * Names and taglines come from each game's own namespace, through the registry: the core
 * never names a game.
 */
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getGame } from "@/games/registry";
import { useTenant } from "@/core/tenant/TenantProvider";
import { useGames, type GameSummary } from "./api";
import { useNumber } from "./format";
import { DuelsSection } from "@/core/duels/DuelsSection";

export function GamesPage() {
  const { t } = useTranslation();
  const tenant = useTenant();
  const on = tenant.enabled_games.length > 0;
  const hub = useGames({ enabled: on });
  if (!on) return <p className="bt-page-message">{t("games.disabled")}</p>;
  if (hub.isPending) return <p className="bt-page-message">{t("app.loading")}</p>;
  if (hub.isError || !hub.data) {
    return <p className="bt-page-message bt-page-message--error">{t("games.load_error")}</p>;
  }
  const games = hub.data.games.filter((game) => getGame(game.key));
  return (
    <section className="bt-games">
      <header className="bt-page-header bt-games__header">
        <h1 className="bt-page-title">{t("games.title")}</h1>
        <p className="bt-page-subtitle">{t("games.intro")}</p>
      </header>
      {games.length > 0 ? (
        <section aria-labelledby="games-deals" className="bt-games__section">
          <div className="bt-games__section-head">
            <h2 id="games-deals" className="bt-games__section-title">
              {t("games.deals_title")}
            </h2>
            <p className="bt-games__section-hint">{t("games.deals_hint")}</p>
          </div>
          <ul className="bt-game-index">
            {games.map((game) => (
              <GameRow key={game.key} game={game} dealNumber={hub.data.deal_number} />
            ))}
          </ul>
        </section>
      ) : null}
      <DuelsSection />
    </section>
  );
}

function GameRow({ game, dealNumber }: { game: GameSummary; dealNumber: number }) {
  const { t } = useTranslation();
  const number = useNumber();
  const module = getGame(game.key)!;
  const ns = module.namespace;
  const mine = game.my_ranked_run;
  const best = game.best_today;
  return (
    <li className="bt-game-row">
      <div className="bt-game-row__name">
        <span className="bt-game-row__title">{t("name", { ns })}</span>
        <span className="bt-game-row__tagline">{t("tagline", { ns })}</span>
        <span className="bt-game-row__deal">{t("games.deal_number", { number: dealNumber })}</span>
      </div>
      <p className="bt-game-row__mine">
        {mine && mine.score !== null ? (
          <>
            {t("games.your_run")} <span className="bt-game-row__figure">{number(mine.score)}</span>
            {mine.rank ? (
              <span className="bt-game-row__rank">
                {t("games.rank_of", { rank: mine.rank, count: game.players_today })}
              </span>
            ) : null}
          </>
        ) : (
          <span className="bt-game-row__quiet">{t("games.not_played")}</span>
        )}
      </p>
      <p className="bt-game-row__best">
        {best ? (
          <>
            {t("games.best_today")}{" "}
            <span className="bt-game-row__figure">{number(best.score)}</span>{" "}
            {t("games.best_by", { name: best.participant })}
          </>
        ) : (
          <span className="bt-game-row__quiet">{t("games.nobody_yet")}</span>
        )}
      </p>
      <div className="bt-game-row__actions">
        <Link
          to={`/games/${game.key}`}
          className={`bt-button bt-button--sm ${mine ? "bt-button--secondary" : "bt-button--primary"}`}
        >
          {mine ? t("games.play_again") : t("games.play_daily")}
        </Link>
        <Link to={`/games/${game.key}?mode=free`} className="bt-game-row__free">
          {t("games.free_play")}
        </Link>
      </div>
    </li>
  );
}
